import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import WindowPicker from './WindowPicker.jsx';

/**
 * "Why is demand low" -- with each candidate cause labelled by how well the data supports it.
 *
 * WHY THE STATUS LABEL IS THE POINT
 * ---------------------------------
 * This endpoint deliberately separates OBSERVED_FACT from UNKNOWN, and the distinction is the
 * whole value of the report. Several of these causes are not answerable from bid outcomes at all:
 * a buyer that deprioritises a path for ads.txt or schain reasons sends no signal, it simply bids
 * less, which is indistinguishable from not wanting the inventory.
 *
 * So the UI must never flatten them into a ranked list of "reasons". An UNKNOWN rendered as a
 * finding is how a team spends a week chasing something the data never said. The two groups are
 * headed separately, the status is on every card, and the evidence string is rendered verbatim
 * rather than summarised -- it is written to be read whole, and every hedge in it is load-bearing.
 *
 * The same-inventory table is included because it is the evidence the strongest cause rests on:
 * partners seeing byte-for-byte comparable traffic, side by side. Everything that could otherwise
 * explain a bid-rate gap -- supply, geography, format, floor, request shape -- is held constant by
 * construction there, which is what makes the remaining spread attributable at all.
 */

const STATUS_BADGE = {
  OBSERVED_FACT: 'status-badge--active',
  UNKNOWN: 'status-badge--neutral',
};

function title(key) {
  return key.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

const pct = (n) => (n === null || n === undefined ? '—' : `${(n * 100).toFixed(4)}%`);
const int = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

function CauseCard({ causeKey, cause }) {
  return (
    <div className="card card--nested" style={{ padding: '14px 16px' }}>
      <div className="card__header">
        <h3 className="card__title">{title(causeKey)}</h3>
        <span className={`status-badge ${STATUS_BADGE[cause.status] || 'status-badge--neutral'}`}>
          {cause.status}
        </span>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: '13.5px', lineHeight: 1.6 }}>{cause.evidence}</p>
      {cause.recommendation && (
        <p className="card__hint" style={{ marginTop: '8px' }}>{cause.recommendation}</p>
      )}
    </div>
  );
}

export default function AttributionTab() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .demandAttribution(days)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(err instanceof ApiError ? err.message : 'Could not load attribution.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [days]);

  const causes = Object.entries(data?.causes || {});
  const observed = causes.filter(([, c]) => c.status === 'OBSERVED_FACT');
  const unknown = causes.filter(([, c]) => c.status !== 'OBSERVED_FACT');
  // The endpoint's own notes call sameInventoryComparison the load-bearing section, and it is --
  // but only where there is enough volume for a bid-rate spread to mean anything. Today's payload
  // carries groups of 32, 18 and 1 calls alongside the 752,638-call one, and a partner that bid
  // zero out of one call has told us nothing. Those are kept (dropping data an operator could
  // reasonably want is worse than showing it) but summarised rather than given a full table, so
  // the group that actually carries the finding is not buried under three that do not.
  const MIN_CALLS_FOR_COMPARISON = 1000;
  const allComparisons = (data?.sameInventoryComparison || [])
    .slice()
    .sort((a, b) => (b.totalCalls || 0) - (a.totalCalls || 0));
  const comparisons = allComparisons.filter((c) => (c.totalCalls || 0) >= MIN_CALLS_FOR_COMPARISON);
  const lowVolume = allComparisons.filter((c) => (c.totalCalls || 0) < MIN_CALLS_FOR_COMPARISON);
  const notes = data?.notes || [];

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Demand attribution</h2>
          <WindowPicker days={days} onChange={setDays} disabled={loading} />
        </div>
        <p className="card__hint">
          Candidate explanations for the bid rate, each labelled by what the data actually supports.
          An <b>UNKNOWN</b> is not a weak finding — it means this question cannot be answered from
          bid outcomes, and treating it as a cause is how a week gets spent on the wrong thing.
        </p>

        {loading && <LoadingState label="Loading attribution" />}
        {error && !loading && <EmptyState title="Couldn't load attribution" message={error} />}

        {!loading && !error && causes.length === 0 && (
          <EmptyState title="No attribution available" message="No causes were returned for this window." />
        )}

        {!loading && !error && causes.length > 0 && (
          <div style={{ marginTop: '14px' }}>
            {observed.length > 0 && (
              <>
                <h3 className="card__title" style={{ marginBottom: '10px' }}>
                  Supported by the data
                </h3>
                {observed.map(([k, c]) => <CauseCard key={k} causeKey={k} cause={c} />)}
              </>
            )}
            {unknown.length > 0 && (
              <>
                <h3 className="card__title" style={{ margin: '20px 0 10px' }}>
                  Not answerable from bid outcomes
                </h3>
                {unknown.map(([k, c]) => <CauseCard key={k} causeKey={k} cause={c} />)}
              </>
            )}
          </div>
        )}
      </section>

      {!loading && !error && comparisons.map((cmp, i) => (
        <section className="card" key={`${cmp.publisherId}-${cmp.format}-${i}`}>
          <div className="card__header">
            <h2 className="card__title">
              Same inventory — publisher {cmp.publisherId} / {cmp.format || 'unspecified format'}
            </h2>
            <span className="status-badge status-badge--neutral">
              {int(cmp.totalCalls)} calls · {cmp.distinctPartners} partners
            </span>
          </div>
          <p className="card__hint">
            These partners saw comparable traffic on the same publisher and format. Supply,
            geography, format, floor and request shape are held constant here, so a spread in bid
            rate across these rows is partner-side by construction.
          </p>
          <div className="table-scroll" style={{ marginTop: '14px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th className="num">Calls</th>
                  <th className="num">Response rate</th>
                  <th className="num">Bids</th>
                  <th className="num">Bid rate</th>
                  <th className="num">Wins</th>
                </tr>
              </thead>
              <tbody>
                {(cmp.partners || []).map((p) => (
                  <tr key={p.partnerCode}>
                    <td className="cell-title">{p.partnerCode}</td>
                    <td className="num">{int(p.calls)}</td>
                    <td className="num cell-muted">{pct(p.responseRate)}</td>
                    <td className="num">{int(p.bids)}</td>
                    <td className="num">{pct(p.bidRate)}</td>
                    <td className="num">{int(p.wins)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {!loading && !error && lowVolume.length > 0 && (
        <section className="card">
          <div className="card__header">
            <h2 className="card__title">Low-volume groups</h2>
          </div>
          <p className="card__hint">
            Also compared, but under {MIN_CALLS_FOR_COMPARISON.toLocaleString()} calls. A bid-rate
            difference at this volume is not evidence of anything — a partner that bid zero out of
            one call has told us nothing.
          </p>
          <div className="table-scroll" style={{ marginTop: '14px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Publisher</th>
                  <th>Format</th>
                  <th className="num">Partners</th>
                  <th className="num">Calls</th>
                </tr>
              </thead>
              <tbody>
                {lowVolume.map((cmp, i) => (
                  <tr key={`lv-${cmp.publisherId}-${cmp.format}-${i}`}>
                    <td className="cell-title">Publisher {cmp.publisherId}</td>
                    <td className="cell-muted">{cmp.format || 'unspecified'}</td>
                    <td className="num">{cmp.distinctPartners}</td>
                    <td className="num">{int(cmp.totalCalls)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && !error && notes.length > 0 && (
        <section className="card">
          <div className="card__header">
            <h2 className="card__title">How to read this</h2>
          </div>
          <ul className="plain-list">
            {notes.map((n, i) => (
              <li key={i} className="card__hint" style={{ marginBottom: '8px', lineHeight: 1.6 }}>
                {n}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
